import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useCompetition } from '../contexts/CompetitionContext';
import { adminBulkDeleteGroups, competitionGroupsPath } from '../services/competitionApi';
import { getCompetitionDisplayName } from '../utils/competitionDisplayName';
import { getTeamDisplayName } from '../utils/teamDisplayName';
import { downloadCsvUtf8Bom, readCsvFileAsUtf8 } from '../utils/csvUtf8';
import axios from 'axios';
import toast from 'react-hot-toast';
import { Plus, Edit2, Trash2, Users, Upload, Download, FileText, Flag, CheckSquare, Square } from 'lucide-react';
import { AdminLayout } from '../components/layout/AdminLayout';
import { Button } from '../components/ui/Button';

const AdminTeams = () => {
  const { t, language, isRTL } = useLanguage();
  const { competitionSlug, currentCompetition } = useCompetition();
  const groupsUrl = () => competitionGroupsPath(competitionSlug);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingTeam, setEditingTeam] = useState(null);
  const [teamForm, setTeamForm] = useState({
    nameHe: '',
    nameEn: '',
    logoUrl: '',
    description: ''
  });
  const [selectedTeamIds, setSelectedTeamIds] = useState([]);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  useEffect(() => {
    fetchTeams();
    setSelectedTeamIds([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [competitionSlug]);

  const fetchTeams = async () => {
    try {
      setLoading(true);
      const response = await axios.get(groupsUrl());
      setTeams(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching teams:', error);
      toast.error(language === 'en' ? 'Failed to load teams' : 'נכשל בטעינת הקבוצות');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!teamForm.nameHe.trim()) {
      toast.error(language === 'en' ? 'Hebrew name is required' : 'שם בעברית נדרש');
      return;
    }
    if (!teamForm.nameEn.trim()) {
      toast.error(language === 'en' ? 'English name is required' : 'שם באנגלית נדרש');
      return;
    }

    try {
      if (editingTeam) {
        await axios.put(`${groupsUrl()}/${editingTeam._id}`, teamForm);
        toast.success(language === 'en' ? 'Team updated successfully!' : 'הקבוצה עודכנה בהצלחה!');
      } else {
        await axios.post(groupsUrl(), teamForm);
        toast.success(language === 'en' ? 'Team created successfully!' : 'הקבוצה נוצרה בהצלחה!');
      }
      
      setShowModal(false);
      setEditingTeam(null);
      setTeamForm({ nameHe: '', nameEn: '', logoUrl: '', description: '' });
      fetchTeams();
    } catch (error) {
      const message = error.response?.data?.message || (language === 'en' ? 'Failed to save team' : 'נכשל בשמירת הקבוצה');
      toast.error(message);
    }
  };

  const handleEdit = (team) => {
    setEditingTeam(team);
    setTeamForm({
      nameHe: team.nameHe ?? team.name ?? '',
      nameEn: team.nameEn ?? team.name ?? '',
      logoUrl: team.logoUrl || '',
      description: team.description || ''
    });
    setShowModal(true);
  };

  const toggleTeamSelection = (teamId) => {
    setSelectedTeamIds((prev) =>
      prev.includes(teamId) ? prev.filter((id) => id !== teamId) : [...prev, teamId]
    );
  };

  const allTeamsSelected = teams.length > 0 && selectedTeamIds.length === teams.length;

  const toggleSelectAllTeams = () => {
    if (selectedTeamIds.length === teams.length) {
      setSelectedTeamIds([]);
    } else {
      setSelectedTeamIds(teams.map((team) => team._id));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedTeamIds.length === 0) return;

    const count = selectedTeamIds.length;
    const confirmMessage =
      language === 'en'
        ? `Delete ${count} selected team${count === 1 ? '' : 's'}? This cannot be undone.`
        : `למחוק ${count} קבוצות שנבחרו? לא ניתן לבטל פעולה זו.`;

    if (!window.confirm(confirmMessage)) return;

    try {
      setBulkDeleting(true);
      const result = await adminBulkDeleteGroups(competitionSlug, selectedTeamIds);
      const deletedCount = result.deleted?.length || 0;
      const skippedCount = result.skipped?.length || 0;

      if (deletedCount > 0) {
        toast.success(
          language === 'en'
            ? `Deleted ${deletedCount} team${deletedCount === 1 ? '' : 's'}`
            : `נמחקו ${deletedCount} קבוצות`
        );
      }
      if (skippedCount > 0) {
        toast.error(
          language === 'en'
            ? `${skippedCount} team${skippedCount === 1 ? '' : 's'} could not be deleted (used in games or not found)`
            : `${skippedCount} קבוצות לא נמחקו (משמשות במשחקים או לא נמצאו)`
        );
      }

      setSelectedTeamIds([]);
      fetchTeams();
    } catch (error) {
      const message =
        error.response?.data?.message ||
        (language === 'en' ? 'Failed to delete selected teams' : 'נכשל במחיקת הקבוצות שנבחרו');
      toast.error(message);
    } finally {
      setBulkDeleting(false);
    }
  };

  const handleDelete = async (teamId) => {
    if (window.confirm(language === 'en' ? 'Are you sure you want to delete this team?' : 'האם אתה בטוח שברצונך למחוק את הקבוצה הזו?')) {
      try {
        await axios.delete(`${groupsUrl()}/${teamId}`);
        toast.success(language === 'en' ? 'Team deleted successfully!' : 'הקבוצה נמחקה בהצלחה!');
        fetchTeams();
      } catch (error) {
        const message = error.response?.data?.message || (language === 'en' ? 'Failed to delete team' : 'נכשל במחיקת הקבוצה');
        toast.error(message);
      }
    }
  };

  // CSV Import Functions
  const handleCSVUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.name.endsWith('.csv')) {
      toast.error(language === 'en' ? 'Please select a CSV file' : 'אנא בחר קובץ CSV');
      return;
    }

    const text = await readCsvFileAsUtf8(file);
    const lines = text.split(/\r?\n/).filter((line) => line.trim());
    const headers = lines[0]
      .split(',')
      .map((h) => h.trim().replace(/^\uFEFF/, '').toLowerCase());

    const hasBilingual = headers.includes('namehe') && headers.includes('nameen');
    const hasLegacyName = headers.includes('name');
    if (!hasBilingual && !hasLegacyName) {
      toast.error(
        language === 'en'
          ? 'CSV must include nameHe + nameEn, or legacy name column'
          : 'CSV חייב לכלול nameHe + nameEn, או עמודת name'
      );
      return;
    }

    try {
      const teamsToCreate = [];
      
      for (let i = 1; i < lines.length; i++) {
        const values = lines[i].split(',').map(v => v.trim());
        const teamData = {};
        
        headers.forEach((header, index) => {
          const key = header.toLowerCase();
          teamData[key] = values[index] || '';
        });

        const nameHe = teamData.namehe || teamData.name || '';
        const nameEn = teamData.nameen || teamData.name || '';
        if (!nameHe.trim() && !nameEn.trim()) {
          continue;
        }

        teamsToCreate.push({
          nameHe: nameHe.trim() || nameEn.trim(),
          nameEn: nameEn.trim() || nameHe.trim(),
          logoUrl: teamData.logourl || teamData.logoUrl || '',
          description: teamData.description || ''
        });
      }

      // Create teams
      let successCount = 0;
      for (const teamData of teamsToCreate) {
        try {
          await axios.post(groupsUrl(), teamData);
          successCount++;
        } catch (error) {
          console.error('Error creating team:', error);
        }
      }

      toast.success(`${language === 'en' ? 'Successfully imported' : 'יובאו בהצלחה'} ${successCount} ${language === 'en' ? 'teams' : 'קבוצות'}`);
      fetchTeams();
    } catch (error) {
      console.error('CSV import error:', error);
      toast.error(language === 'en' ? 'Failed to import CSV' : 'נכשל ביבוא CSV');
    }

    // Reset file input
    event.target.value = '';
  };

  const downloadSampleCSV = () => {
    const sampleData = [
      'nameHe,nameEn,logoUrl,description',
      'מנצ׳סטר יונייטד,Manchester United,https://example.com/logo1.png,Sample team',
      'ריאל מדריד,Real Madrid,https://example.com/logo2.png,Sample team',
      'באיירן מינכן,Bayern Munich,,Sample team'
    ];

    downloadCsvUtf8Bom(sampleData.join('\n'), 'sample_teams.csv');
  };

  const downloadWorldCup2026TeamsCSV = async () => {
    try {
      const res = await fetch(`${process.env.PUBLIC_URL || ''}/world-cup-2026-teams.csv`);
      if (!res.ok) throw new Error('fetch failed');
      const text = await res.text();
      downloadCsvUtf8Bom(text, 'world-cup-2026-teams.csv');
    } catch {
      toast.error(
        language === 'en'
          ? 'Could not download World Cup teams file'
          : 'לא ניתן להוריד את קובץ נבחרות המונדיאל'
      );
    }
  };

  const competitionLine =
    getCompetitionDisplayName(currentCompetition, language) ||
    competitionSlug ||
    '—';

  if (loading) {
    return (
      <AdminLayout
        title={t('manageTeams')}
        subtitle={
          language === 'en'
            ? `Competition: ${competitionLine}`
            : `תחרות: ${competitionLine}`
        }
        loading
      />
    );
  }

  return (
    <AdminLayout
      title={t('manageTeams')}
      subtitle={
        language === 'en'
          ? `${competitionLine} · ${teams.length} team${teams.length === 1 ? '' : 's'} (use top bar to switch competition)`
          : `${competitionLine} · ${teams.length} קבוצות (החלף תחרות בשורת העליונה)`
      }
      actions={
        <>
          <Button variant="success" size="sm" onClick={downloadSampleCSV}>
            <Download className="h-4 w-4" />
            {t('downloadSample')}
          </Button>
          <Button variant="secondary" size="sm" onClick={downloadWorldCup2026TeamsCSV}>
            <Flag className="h-4 w-4" />
            {language === 'en' ? 'WC 2026 teams (48)' : 'מונדיאל 2026 (48)'}
          </Button>
          <label className="inline-flex cursor-pointer items-center gap-2 rounded-xl bg-brand-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-brand-700">
            <Upload className="h-4 w-4" />
            {t('importCSV')}
            <input
              type="file"
              accept=".csv"
              onChange={handleCSVUpload}
              className="hidden"
            />
          </label>
          {teams.length > 0 && (
            <Button variant="secondary" size="sm" onClick={toggleSelectAllTeams}>
              {allTeamsSelected ? (
                <Square className="h-4 w-4" />
              ) : (
                <CheckSquare className="h-4 w-4" />
              )}
              {allTeamsSelected
                ? language === 'en'
                  ? 'Deselect all'
                  : 'בטל בחירה'
                : language === 'en'
                  ? 'Select all'
                  : 'בחר הכל'}
            </Button>
          )}
          {teams.length > 0 && selectedTeamIds.length > 0 && (
            <Button
              variant="danger"
              size="sm"
              onClick={handleBulkDelete}
              disabled={bulkDeleting}
            >
              <Trash2 className="h-4 w-4" />
              {language === 'en'
                ? `Delete selected (${selectedTeamIds.length})`
                : `מחק נבחרות (${selectedTeamIds.length})`}
            </Button>
          )}
          <Button
            variant="primary"
            size="sm"
            onClick={() => {
              setEditingTeam(null);
              setTeamForm({ nameHe: '', nameEn: '', logoUrl: '', description: '' });
              setShowModal(true);
            }}
          >
            <Plus className="h-4 w-4" />
            {t('addTeam')}
          </Button>
        </>
      }
    >
        {/* CSV Import Guide */}
        <div className="mb-6 rounded-2xl border border-brand-200 bg-brand-50 p-4">
          <div className="mb-2 flex items-center gap-2">
            <FileText className="h-4 w-4 text-brand-700" />
            <h3 className="text-sm font-semibold text-brand-900">{t('csvImportGuide')}</h3>
          </div>
          <div className="text-sm leading-relaxed text-brand-800">
            <p className="mb-2 font-medium text-brand-900">
              {language === 'en'
                ? `Imports and edits apply only to: ${competitionLine}`
                : `הייבוא והעריכה חלים רק על: ${competitionLine}`}
            </p>
            <strong>{t('requiredColumns')}:</strong> nameHe, nameEn (or legacy name for both)<br/>
            <strong>{t('optionalColumns')}:</strong> logoUrl, description<br/>
            <strong>{t('example')}:</strong> ארגנטינה,Argentina,https://flagcdn.com/w320/ar.png,FIFA WC 2026
            <br />
            <span className="mt-2 block text-brand-700">
              {language === 'en'
                ? 'Excel: use “WC 2026 teams (48)” download (UTF-8 with BOM). If Hebrew looks wrong, do not re-save from Excel without UTF-8 — re-download from the app.'
                : 'Excel: הורד “מונדיאל 2026 (48)” (UTF-8 עם BOM). אם העברית נראית שגויה — אל תשמור מחדש מאקסל בלי UTF-8; הורד שוב מהאפליקציה.'}
            </span>
          </div>
        </div>

        {teams.length > 0 && (
          <div className="mb-4 flex flex-wrap items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
            <Button variant="secondary" size="sm" onClick={toggleSelectAllTeams}>
              {allTeamsSelected ? (
                <Square className="h-4 w-4" />
              ) : (
                <CheckSquare className="h-4 w-4" />
              )}
              {allTeamsSelected
                ? language === 'en'
                  ? 'Deselect all'
                  : 'בטל בחירה'
                : language === 'en'
                  ? 'Select all'
                  : 'בחר הכל'}
            </Button>
            {selectedTeamIds.length > 0 && (
              <span className="text-sm text-slate-600">
                {language === 'en'
                  ? `${selectedTeamIds.length} selected`
                  : `${selectedTeamIds.length} נבחרו`}
              </span>
            )}
          </div>
        )}

        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {teams.map((team) => (
            <div
              key={team._id}
              className={`rounded-2xl border bg-white p-5 shadow-md shadow-slate-900/5 ${
                selectedTeamIds.includes(team._id)
                  ? 'border-brand-400 ring-2 ring-brand-100'
                  : 'border-slate-200'
              }`}
            >
              <div className="mb-4 flex items-start gap-3">
                <input
                  type="checkbox"
                  checked={selectedTeamIds.includes(team._id)}
                  onChange={() => toggleTeamSelection(team._id)}
                  className="mt-1 h-4 w-4 shrink-0 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
                  aria-label={
                    language === 'en'
                      ? `Select ${getTeamDisplayName(team, language)}`
                      : `בחר ${getTeamDisplayName(team, language)}`
                  }
                />
                <div className="flex min-w-0 flex-1 items-center gap-4">
                <img
                  src={team.logoUrl || '/default-team.png'}
                  alt={getTeamDisplayName(team, language)}
                  className="h-14 w-14 rounded-full border-2 border-slate-200 object-cover"
                />
                <div className="min-w-0 flex-1">
                  <h3 className="text-lg font-bold text-slate-900">
                    {getTeamDisplayName(team, language)}
                  </h3>
                  {(team.nameHe || team.nameEn) && (
                    <p className="mt-0.5 text-xs text-slate-500">
                      {language === 'en' ? team.nameHe : team.nameEn}
                    </p>
                  )}
                  {team.description && (
                    <p className="mt-1 text-sm text-slate-600">{team.description}</p>
                  )}
                </div>
                </div>
              </div>

              <div className="flex justify-end gap-2">
                <Button variant="primary" size="sm" onClick={() => handleEdit(team)}>
                  <Edit2 className="h-4 w-4" />
                  {t('edit')}
                </Button>
                <Button variant="danger" size="sm" onClick={() => handleDelete(team._id)}>
                  <Trash2 className="h-4 w-4" />
                  {t('delete')}
                </Button>
              </div>
            </div>
          ))}

          {teams.length === 0 && (
            <div className="col-span-full flex flex-col items-center py-16 text-slate-400">
              <Users className="mb-4 h-16 w-16 opacity-40" />
              <p className="text-lg">{language === 'en' ? 'No teams yet' : 'אין קבוצות עדיין'}</p>
              <p className="mt-2 text-sm text-slate-500">
                {language === 'en'
                  ? 'Add a team or import CSV to get started.'
                  : 'הוסף קבוצה או ייבא CSV כדי להתחיל.'}
              </p>
            </div>
          )}
        </div>

        {/* Team Form Modal */}
        {showModal && (
          <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0, 0, 0, 0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
            <div style={{ background: 'white', borderRadius: 16, padding: 32, maxWidth: 500, width: '90%', boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)' }}>
              <h2 style={{ fontSize: 24, fontWeight: 700, color: '#18181b', marginBottom: 24, textAlign: 'center' }}>
                {editingTeam ? t('editTeam') : t('addTeam')}
              </h2>
              
              <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                <div>
                  <label style={{ display: 'block', fontWeight: 600, color: '#18181b', marginBottom: 8, textAlign: isRTL ? 'right' : 'left' }}>
                    {language === 'en' ? 'Name (Hebrew)' : 'שם בעברית'} *
                  </label>
                  <input
                    type="text"
                    value={teamForm.nameHe}
                    onChange={(e) => setTeamForm({ ...teamForm, nameHe: e.target.value })}
                    required
                    dir="rtl"
                    placeholder={language === 'en' ? 'e.g. ארגנטינה' : 'למשל ארגנטינה'}
                    style={{ 
                      width: '100%', 
                      padding: 12, 
                      borderRadius: 8, 
                      border: '1.5px solid #fee2e2', 
                      background: 'white', 
                      color: '#18181b', 
                      fontSize: 16 
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontWeight: 600, color: '#18181b', marginBottom: 8, textAlign: isRTL ? 'right' : 'left' }}>
                    {language === 'en' ? 'Name (English)' : 'שם באנגלית'} *
                  </label>
                  <input
                    type="text"
                    value={teamForm.nameEn}
                    onChange={(e) => setTeamForm({ ...teamForm, nameEn: e.target.value })}
                    required
                    dir="ltr"
                    placeholder={language === 'en' ? 'e.g. Argentina' : 'למשל Argentina'}
                    style={{ 
                      width: '100%', 
                      padding: 12, 
                      borderRadius: 8, 
                      border: '1.5px solid #fee2e2', 
                      background: 'white', 
                      color: '#18181b', 
                      fontSize: 16 
                    }}
                  />
                </div>
                
                <div>
                  <label style={{ display: 'block', fontWeight: 600, color: '#18181b', marginBottom: 8, textAlign: isRTL ? 'right' : 'left' }}>
                    {t('logoUrl')}
                  </label>
                  <input
                    type="url"
                    value={teamForm.logoUrl}
                    onChange={(e) => setTeamForm({ ...teamForm, logoUrl: e.target.value })}
                    placeholder={language === 'en' ? 'https://example.com/logo.png' : 'https://example.com/logo.png'}
                    style={{ 
                      width: '100%', 
                      padding: 12, 
                      borderRadius: 8, 
                      border: '1.5px solid #fee2e2', 
                      background: 'white', 
                      color: '#18181b', 
                      fontSize: 16 
                    }}
                  />
                </div>
                
                <div>
                  <label style={{ display: 'block', fontWeight: 600, color: '#18181b', marginBottom: 8, textAlign: isRTL ? 'right' : 'left' }}>
                    {t('description')}
                  </label>
                  <textarea
                    value={teamForm.description}
                    onChange={(e) => setTeamForm({ ...teamForm, description: e.target.value })}
                    placeholder={language === 'en' ? 'Enter team description (optional)' : 'הכנס תיאור קבוצה (אופציונלי)'}
                    rows={3}
                    style={{ 
                      width: '100%', 
                      padding: 12, 
                      borderRadius: 8, 
                      border: '1.5px solid #fee2e2', 
                      background: 'white', 
                      color: '#18181b', 
                      fontSize: 16,
                      resize: 'vertical'
                    }}
                  />
                </div>

                <div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    style={{ 
                      flex: 1, 
                      background: '#fee2e2', 
                      color: '#18181b', 
                      fontWeight: 600, 
                      fontSize: 16, 
                      padding: '12px 0', 
                      borderRadius: 10, 
                      border: 'none', 
                      cursor: 'pointer' 
                    }}
                  >
                    {t('cancel')}
                  </button>
                  <button
                    type="submit"
                    style={{ 
                      flex: 1, 
                      background: 'linear-gradient(90deg, #dc2626 0%, #b91c1c 100%)', 
                      color: 'white', 
                      fontWeight: 600, 
                      fontSize: 16, 
                      padding: '12px 0', 
                      borderRadius: 10, 
                      border: 'none', 
                      cursor: 'pointer', 
                      boxShadow: '0 2px 8px rgba(220, 38, 38, 0.15)' 
                    }}
                  >
                    {editingTeam ? t('updateTeam') : t('createTeam')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
    </AdminLayout>
  );
};

export default AdminTeams; 